import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // If an announcement links to the footer newsletter anchor,
    // ensure the email input gets focused after scrolling.
    this.addEventListener('click', this.#handleAnchorFocus);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #handleAnchorFocus = (event) => {
    const link =
      event.target instanceof Element ? event.target.closest('a[href*="#footer-newsletter"]') : null;
    if (!link) return;

    const href = link.getAttribute('href') || '';
    if (!href.includes('#footer-newsletter')) return;

    // Prevent full navigation/reload; perform in-page scroll + focus.
    event.preventDefault();

    const root = document.getElementById('footer-newsletter');
    const getTarget = () => {
      const input = root?.querySelector('input[type="email"]');
      if (input instanceof HTMLInputElement) return input;

      const fallback = document.querySelector(
        '.footer-content .email-signup__input-group input[type="email"], footer input[type="email"]',
      );
      return fallback instanceof HTMLInputElement ? fallback : null;
    };

    const scrollAndFocus = () => {
      const target = getTarget();
      if (!target) return false;

      // Scroll to the actual email input so the viewport matches user intent.
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus slightly after scroll kicks in.
      window.setTimeout(() => target.focus({ preventScroll: true }), 250);
      return true;
    };

    // Try immediately, then retry a few frames in case footer content is delayed.
    if (scrollAndFocus()) return;

    let tries = 0;
    const retry = () => {
      tries += 1;
      if (scrollAndFocus() || tries >= 10) return;
      requestAnimationFrame(retry);
    };
    requestAnimationFrame(retry);
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
